import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "../components/motion";
import { ChevronRight, Shield, RefreshCw, FileText, Users, AlertTriangle } from "lucide-react";

const LAST_UPDATED = "1 July 2025";

const TABS = [
  { id: "privacy",  label: "Privacy Policy",      icon: Shield },
  { id: "refund",   label: "Refund Policy",        icon: RefreshCw },
  { id: "terms",    label: "Terms & Conditions",   icon: FileText },
  { id: "couple",   label: "Couple Policy",        icon: Users },
  { id: "safety",   label: "Safety Guidelines",    icon: AlertTriangle },
];

/* ── Section heading ── */
function SectionHead({ title }) {
  return (
    <h2
      className="font-cormorant text-white mb-4 mt-12 first:mt-0 pb-3 border-b border-white/[0.06]"
      style={{ fontSize: "1.45rem" }}
    >
      {title}
    </h2>
  );
}

/* ── Bullet list ── */
function BulletList({ items }) {
  return (
    <ul className="space-y-3 text-white/55 text-sm leading-relaxed">
      {items.map((item, i) => (
        <li key={i} className="flex gap-3">
          <span className="text-gold shrink-0 mt-0.5">—</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

/* ── Paragraph ── */
function Para({ children }) {
  return <p className="text-white/55 text-sm leading-relaxed mb-4">{children}</p>;
}

/* ══════════════════════════════════════════════
   POLICY CONTENT
══════════════════════════════════════════════ */

function PrivacyPolicy() {
  return (
    <div>
      <Para>
        At ROOM XIII, your privacy is the foundation of our service. This policy explains what personal
        data we collect, why we collect it, and how we protect it — in plain language, not legalese.
      </Para>

      <SectionHead title="1. Information We Collect" />
      <Para>We collect only what is necessary to complete your booking and deliver our service:</Para>
      <BulletList items={[
        "Identity information: full name and a government-issued photo ID (Aadhaar, Passport, or Driving Licence).",
        "Contact details: email address and phone number.",
        "Booking data: chosen dates, booking type (hourly or overnight), and any special requests.",
        "Payment information: processed securely through Razorpay. We never store your card number on our servers.",
        "Device & usage data: browser type, IP address, and pages visited — used solely to improve our website.",
      ]} />

      <SectionHead title="2. How We Use Your Information" />
      <BulletList items={[
        "To confirm and manage your reservation.",
        "To send booking confirmation, check-in instructions, and receipts.",
        "To verify your identity and comply with local laws.",
        "To respond to enquiries and provide concierge support.",
        "To improve our service based on aggregated, anonymised usage patterns.",
        "We do NOT sell, rent, or share your data with advertisers — ever.",
      ]} />

      <SectionHead title="3. Data Retention" />
      <BulletList items={[
        "Booking records are retained for 5 years for compliance and tax purposes, then permanently deleted.",
        "Marketing communications (if opted in) can be unsubscribed at any time.",
        "You may request deletion of your account and associated data by emailing contact@roomxiii.com.",
      ]} />

      <SectionHead title="4. Data Security" />
      <Para>
        All data is transmitted over HTTPS (TLS 1.3). Personal data is stored in encrypted databases
        with access restricted to authorised personnel only. We conduct regular security audits
        and follow OWASP best practices.
      </Para>

      <SectionHead title="5. Cookies" />
      <BulletList items={[
        "We use essential cookies only — no tracking or advertising cookies.",
        "Session cookies keep you logged in during your visit.",
        "You may disable cookies in your browser settings, though some features may not function correctly.",
      ]} />

      <SectionHead title="6. Your Rights" />
      <BulletList items={[
        "Right to access: request a copy of all data we hold about you.",
        "Right to correction: request that inaccurate data be corrected.",
        "Right to deletion: request that your data be erased (subject to legal retention requirements).",
        "Right to portability: receive your data in a machine-readable format.",
        "To exercise any of these rights, email contact@roomxiii.com.",
      ]} />

      <SectionHead title="7. Third-Party Services" />
      <BulletList items={[
        "Payment processing: Razorpay (their privacy policies apply to payment data).",
        "Maps: Google Maps embed (Google's privacy policy applies).",
        "We do not use Facebook Pixel, Google Ads, or any third-party ad trackers.",
      ]} />

      <SectionHead title="8. Changes to This Policy" />
      <Para>
        We may update this policy periodically. Significant changes will be communicated via email
        to registered users. Continued use of our service after changes constitutes acceptance.
      </Para>
    </div>
  );
}

function RefundPolicy() {
  return (
    <div>
      <Para>
        Our bookings are non-refundable. Please read this section carefully before you book.
      </Para>

      <SectionHead title="1. Non-refundable Bookings" />
      <BulletList items={[
        "All bookings are non-refundable once confirmed.",
        "This applies to cancellations at any time before check-in, no-shows and early check-outs.",
        "You must acknowledge this before confirming a booking, and we store your acceptance.",
        "Full details are on our Cancellation Policy page (/cancellation-policy).",
      ]} />

      <SectionHead title="2. How to Cancel" />
      <BulletList items={[
        "Log in to your account and navigate to 'My Bookings' to cancel online.",
        "Alternatively, WhatsApp or email us at contact@roomxiii.com with your booking reference.",
        "Cancellation time is calculated from when we receive and confirm your request.",
      ]} />

      <SectionHead title="3. Special Circumstances" />
      <BulletList items={[
        "Medical emergencies: provide documentation and we will waive cancellation fees on a case-by-case basis.",
        "Natural disasters or government-mandated restrictions: full refund or free rescheduling offered.",
        "Property becomes unavailable due to our fault: 100% refund plus a 10% goodwill credit on next booking.",
        "Early check-outs: no refund for unused hours.",
      ]} />

      <SectionHead title="4. Rescheduling" />
      <BulletList items={[
        "You may reschedule your booking once at no charge, provided the request is made at least 48 hours in advance.",
        "A second reschedule incurs a ₹500 administrative fee.",
        "Rescheduled bookings are subject to availability.",
      ]} />
    </div>
  );
}

function TermsPolicy() {
  return (
    <div>
      <Para>
        By completing a booking with ROOM XIII, you agree to these Terms & Conditions. Please read
        them carefully before confirming your reservation.
      </Para>

      <SectionHead title="1. Eligibility" />
      <BulletList items={[
        "Both guests must be at least 18 years of age. Valid government-issued ID is required at check-in.",
        "ROOM XIII is exclusively for couples. Solo guests and groups of three or more are not permitted.",
        "We reserve the right to cancel a booking if eligibility requirements are not met, with a full refund.",
      ]} />

      <SectionHead title="2. Booking & Payment" />
      <BulletList items={[
        "Bookings are confirmed only upon receipt of full payment.",
        "Prices displayed are inclusive of GST unless stated otherwise.",
        "We accept UPI, credit/debit cards, and net banking. Cash payments are not accepted.",
        "Booking confirmations are sent to the registered email address. Check your spam folder if not received within 10 minutes.",
      ]} />

      <SectionHead title="3. Check-In & Check-Out" />
      <BulletList items={[
        "Check-in time: as per your booking slot. Early check-in is subject to availability and may incur additional charges.",
        "Check-out must be completed by the agreed time. Late check-out (without prior approval) is charged at ₹500 per 30 minutes.",
        "Self check-in via smart lock — access codes are shared 1 hour before your slot via SMS and WhatsApp.",
        "Both guests must be present during check-in. One guest cannot check in on behalf of the other.",
      ]} />

      <SectionHead title="4. House Rules" />
      <BulletList items={[
        "No smoking inside the room. Violation incurs a deep-cleaning fee of ₹5,000.",
        "No pets permitted.",
        "Noise levels must be respectful — this is a private residential property.",
        "No additional guests beyond the two registered are permitted on the premises.",
        "Do not tamper with smoke detectors, fire alarms, or any safety equipment.",
        "Candles and open flames are not permitted.",
      ]} />

      <SectionHead title="5. Damages & Liability" />
      <BulletList items={[
        "Guests are responsible for any damage caused to the property or its contents during their stay.",
        "Damage charges reflect actual repair or replacement costs and will be billed to the registered payment method.",
        "ROOM XIII is not liable for loss, theft, or damage to guests' personal belongings.",
        "We maintain comprehensive property insurance — guests' liability is limited to damage directly caused by them.",
      ]} />

      <SectionHead title="6. Intellectual Property" />
      <BulletList items={[
        "All content on this website — including text, images, and design — is the property of ROOM XIII.",
        "Guests may not photograph or record the property's interior for commercial purposes without written consent.",
      ]} />

      <SectionHead title="7. Governing Law" />
      <Para>
        These Terms are governed by the laws of India. Any disputes shall be subject to the exclusive
        jurisdiction of the courts of Chandigarh / Mohali, Punjab.
      </Para>

      <SectionHead title="8. Amendments" />
      <Para>
        ROOM XIII reserves the right to modify these Terms at any time. Updated terms will be posted
        on this page with a revised date. Continued use of our services after changes constitutes
        acceptance of the new terms.
      </Para>
    </div>
  );
}

function CouplePolicy() {
  return (
    <div>
      <Para>
        ROOM XIII is a couples-only private stay. This policy exists to ensure the safety, comfort,
        and privacy of every guest who walks through our door.
      </Para>

      <SectionHead title="1. Who May Book" />
      <BulletList items={[
        "ROOM XIII is exclusively for consenting adult couples (18+).",
        "We welcome all couples regardless of gender, sexual orientation, or marital status — no exceptions, no discrimination.",
        "Solo guests are not permitted. Bookings require exactly two registered guests.",
        "Groups of three or more are not permitted under any circumstances.",
      ]} />

      <SectionHead title="2. Identity Verification" />
      <BulletList items={[
        "Both guests must present a valid, government-issued photo ID at check-in.",
        "Accepted IDs: Aadhaar card, Passport, Driving Licence, or Voter ID.",
        "The name on the ID must match the name on the booking.",
        "Failure to present valid ID will result in denial of check-in with no refund.",
      ]} />

      <SectionHead title="3. Privacy & Non-Discrimination" />
      <BulletList items={[
        "We do not record, share, or disclose the identity of our guests to any third party except as required by law.",
        "Our staff operate on a strict 'do not disturb' policy — we do not enter the property during your stay.",
        "We do not ask about, record, or make judgements based on the nature of your relationship.",
        "All guest information is treated with the same level of confidentiality as a medical record.",
      ]} />

      <SectionHead title="4. Guest Safety" />
      <BulletList items={[
        "If either guest feels unsafe or needs assistance, our concierge is reachable 24/7 via WhatsApp.",
        "Emergency services can be reached at 112. Local emergency numbers are displayed inside the room.",
        "Guests experiencing a medical emergency should call 108 (ambulance) immediately.",
      ]} />
    </div>
  );
}

function SafetyPolicy() {
  return (
    <div>
      <Para>
        Your safety is as important to us as your privacy. Here is what we have in place to ensure
        every stay is secure.
      </Para>

      <SectionHead title="1. Property Safety Features" />
      <BulletList items={[
        "Functioning smoke detectors installed and tested monthly.",
        "CO₂ detector installed in sleeping areas.",
        "Fire extinguisher accessible inside the room.",
        "Emergency exit routes are clearly marked.",
        "First-aid kit stocked and inspected before each booking.",
        "Smart lock with unique access codes generated per booking — previous codes are automatically invalidated.",
      ]} />

      <SectionHead title="2. CCTV & Surveillance" />
      <BulletList items={[
        "CCTV cameras are installed at the building entrance and common corridors ONLY.",
        "There are absolutely NO cameras inside the room, bathroom, or private spaces.",
        "Building entrance footage is retained for 30 days and is accessible only by authorised personnel or law enforcement with a valid order.",
      ]} />

      <SectionHead title="3. Hygiene Protocols" />
      <BulletList items={[
        "Deep cleaning and sanitisation between every single booking — no exceptions.",
        "Hotel-grade linen laundered at 60°C+ and replaced with every new guest.",
        "All surfaces, door handles, and remote controls are sanitised with hospital-grade disinfectant.",
        "Cleaning is completed and verified before your access code is activated.",
      ]} />

      <SectionHead title="4. Emergency Contacts" />
      <BulletList items={[
        "ROOM XIII Concierge (24/7): +91 7018595304 (WhatsApp)",
        "Police: 100 | Ambulance: 108 | Fire: 101 | Universal Emergency: 112",
        "Nearest hospital information is displayed on the welcome card inside the room.",
      ]} />

      <SectionHead title="5. Prohibited Activities" />
      <BulletList items={[
        "Any illegal activities on the premises will result in immediate termination of the booking and reporting to local authorities.",
        "Bringing weapons, illegal substances, or flammable materials onto the property is strictly prohibited.",
        "Violating any safety guideline gives ROOM XIII the right to terminate the booking without refund.",
      ]} />
    </div>
  );
}

const CONTENT = {
  privacy: <PrivacyPolicy />,
  refund:  <RefundPolicy />,
  terms:   <TermsPolicy />,
  couple:  <CouplePolicy />,
  safety:  <SafetyPolicy />,
};

export default function PoliciesPage() {
  const [active, setActive] = useState("privacy");
  const contentRef = useRef(null);

  const scrollToContent = () => {
    if (contentRef.current && window.innerWidth < 1024) {
      contentRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const switchTab = (id) => {
    setActive(id);
    scrollToContent();
  };

  return (
    <div className="grain min-h-screen" style={{ background: "var(--bg-base)" }} data-testid="policies-page">

      {/* ── Header ── */}
      <div className="relative overflow-hidden" style={{ paddingTop: "9rem", paddingBottom: "5rem" }}>
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "radial-gradient(ellipse 70% 50% at 50% 0%, rgba(201,162,75,0.07) 0%, transparent 70%)"
        }} />
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            className="eyebrow"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Transparency
          </motion.div>
          <div style={{ overflow: "hidden" }}>
            <motion.h1
              className="font-cormorant text-white mt-3 leading-none"
              style={{ fontSize: "clamp(3rem, 7vw, 5.5rem)" }}
              initial={{ y: "100%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 0.85, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
              Policies
            </motion.h1>
          </div>
          <motion.p
            className="text-white/35 text-xs mt-4 tracking-wide"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            Last updated: {LAST_UPDATED}
          </motion.p>
        </div>
      </div>

      {/* ── Body: sidebar + content ── */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 pb-32">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">

          {/* ── Sidebar ── */}
          <motion.aside
            className="lg:w-64 shrink-0"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            {/* Mobile: horizontal scroll */}
            <div className="lg:sticky lg:top-28">
              <div className="text-[10px] uppercase tracking-[0.24em] text-white/30 mb-4 hidden lg:block">
                Contents
              </div>
              <nav className="flex lg:flex-col gap-1 overflow-x-auto pb-2 lg:pb-0">
                {TABS.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = active === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => switchTab(tab.id)}
                      className="flex items-center gap-3 px-4 py-3 text-left transition-all duration-300 shrink-0 lg:shrink border-b-2 lg:border-b-0 lg:border-l-2"
                      style={{
                        borderColor: isActive ? "var(--gold)" : "transparent",
                        background: isActive ? "rgba(201,162,75,0.06)" : "transparent",
                        color: isActive ? "var(--gold)" : "rgba(255,255,255,0.4)",
                      }}
                    >
                      <Icon size={13} className="shrink-0" />
                      <span className="text-xs tracking-wide whitespace-nowrap">{tab.label}</span>
                      {isActive && <ChevronRight size={11} className="ml-auto hidden lg:block" />}
                    </button>
                  );
                })}
              </nav>

              {/* Contact callout */}
              <div
                className="hidden lg:block mt-10 p-5 border border-white/[0.04]"
                style={{ background: "rgba(201,162,75,0.04)" }}
              >
                <div className="text-[10px] uppercase tracking-[0.2em] text-gold mb-2">Questions?</div>
                <p className="text-white/40 text-xs leading-relaxed mb-3">
                  Our team can clarify any policy in plain language.
                </p>
                <a
                  href="mailto:contact@roomxiii.com"
                  className="text-gold text-xs hover:text-gold-bright transition-colors"
                >
                  contact@roomxiii.com
                </a>
              </div>
            </div>
          </motion.aside>

          {/* ── Main content ── */}
          <motion.main
            ref={contentRef}
            className="flex-1 min-w-0"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
          >
            {/* Active tab label */}
            <div className="flex items-center justify-between mb-8 pb-6 border-b border-white/[0.06]">
              <h2 className="font-cormorant text-white" style={{ fontSize: "clamp(1.6rem, 3vw, 2.2rem)" }}>
                {TABS.find((t) => t.id === active)?.label}
              </h2>
              <span className="text-white/25 text-xs hidden sm:block">Last updated: {LAST_UPDATED}</span>
            </div>

            {/* Animated content swap */}
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              >
                {CONTENT[active]}
              </motion.div>
            </AnimatePresence>

            {/* Bottom nav */}
            <div className="flex gap-4 mt-16 pt-8 border-t border-white/[0.06]">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => switchTab(tab.id)}
                  className="text-xs transition-colors"
                  style={{ color: active === tab.id ? "var(--gold)" : "rgba(255,255,255,0.3)" }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </motion.main>
        </div>
      </div>
    </div>
  );
}
